// Premium template system for Living Announcements
// Each template has a DISTINCT visual layout and emotional structure
// Templates designed to work beautifully with text-only content

// 6 emotional intent categories (previously called templates)
export type TemplateType = 'apology' | 'gratitude' | 'hard_conversation' | 'love' | 'closure' | 'custom' | 'birthday' | 'anniversary' | 'celebration' | 'encouragement';

// Legacy templates (mapped to new layouts for backwards compatibility)
export type LegacyTemplateType = 'classic' | 'modern' | 'bold' | 'journal' | 'letter' | 'headline' | 'moment' | 'reflection' | 'keepsake';

// All possible template values from database
export type AnyTemplateType = TemplateType | LegacyTemplateType;

// AI-generated first lines for each template
export const TEMPLATE_FIRST_LINES: Record<TemplateType, string> = {
  apology: "I've been wanting to say this for a while…",
  gratitude: "I don't say this enough, but…",
  hard_conversation: "There's something I need to share with you…",
  love: "I hope you know how much you mean to me…",
  closure: "I've been holding onto this, and it's time to let go…",
  custom: "There's something on my heart I want to share…",
  birthday: "On your special day, I want you to know…",
  anniversary: "Another year together, and I still feel…",
  celebration: "This moment deserves to be celebrated because…",
  encouragement: "I see how hard you've been working, and I want you to know…",
};

export interface TemplateConfig {
  value: TemplateType;
  label: string;
  description: string;
  aiPrompt: string;
  aiFirstLine: string;
  typography: {
    headingFont: string;
    bodyFont: string;
    headingSize: string;
    bodySize: string;
  };
  colors: {
    background: string;
    text: string;
    accent: string;
  };
  animation: 'fade' | 'reveal' | 'typewriter';
}

export const TEMPLATE_OPTIONS: TemplateConfig[] = [
  {
    value: 'apology',
    label: 'Apology',
    description: 'Express genuine remorse and take responsibility with care.',
    aiPrompt: 'Write a sincere apology that acknowledges wrongdoing, expresses genuine remorse, and takes responsibility. Be humble and honest without making excuses. Focus on the impact of actions and commitment to change.',
    aiFirstLine: TEMPLATE_FIRST_LINES.apology,
    typography: {
      headingFont: 'font-serif',
      bodyFont: 'font-sans',
      headingSize: 'text-2xl',
      bodySize: 'text-lg',
    },
    colors: {
      background: 'bg-slate-50 dark:bg-slate-900',
      text: 'text-slate-800 dark:text-slate-200',
      accent: 'text-slate-600 dark:text-slate-400',
    },
    animation: 'fade',
  },
  {
    value: 'gratitude',
    label: 'Gratitude',
    description: 'Share sincere appreciation and thankfulness.',
    aiPrompt: 'Write a heartfelt expression of gratitude that acknowledges specific ways someone has positively impacted your life. Be specific about what you appreciate and why it matters to you.',
    aiFirstLine: TEMPLATE_FIRST_LINES.gratitude,
    typography: {
      headingFont: 'font-serif',
      bodyFont: 'font-sans',
      headingSize: 'text-2xl',
      bodySize: 'text-lg',
    },
    colors: {
      background: 'bg-amber-50 dark:bg-amber-950/20',
      text: 'text-amber-950 dark:text-amber-100',
      accent: 'text-amber-700 dark:text-amber-400',
    },
    animation: 'fade',
  },
  {
    value: 'hard_conversation',
    label: 'Hard Conversation',
    description: 'Address difficult topics with respect and clarity.',
    aiPrompt: 'Write a thoughtful message that addresses a difficult topic with honesty, empathy, and respect. Be clear about your perspective while remaining open to understanding. Focus on moving forward constructively.',
    aiFirstLine: TEMPLATE_FIRST_LINES.hard_conversation,
    typography: {
      headingFont: 'font-sans',
      bodyFont: 'font-sans',
      headingSize: 'text-xl',
      bodySize: 'text-lg leading-relaxed',
    },
    colors: {
      background: 'bg-slate-100 dark:bg-slate-800',
      text: 'text-slate-900 dark:text-slate-100',
      accent: 'text-primary',
    },
    animation: 'fade',
  },
  {
    value: 'love',
    label: 'Love',
    description: 'Express deep affection and emotional connection.',
    aiPrompt: 'Write a deeply loving message that expresses affection, appreciation, and emotional connection. Share specific moments or qualities that make this person special. Be vulnerable and genuine.',
    aiFirstLine: TEMPLATE_FIRST_LINES.love,
    typography: {
      headingFont: 'font-heading',
      bodyFont: 'font-sans',
      headingSize: 'text-3xl',
      bodySize: 'text-lg',
    },
    colors: {
      background: 'bg-gradient-to-br from-rose-50 to-purple-50 dark:from-rose-950/20 dark:to-purple-950/20',
      text: 'text-rose-900 dark:text-rose-100',
      accent: 'text-rose-600 dark:text-rose-400',
    },
    animation: 'reveal',
  },
  {
    value: 'closure',
    label: 'Closure',
    description: 'Bring resolution and peace to a situation.',
    aiPrompt: 'Write a message that seeks closure and peace. Acknowledge what has passed, express any final thoughts or feelings, and focus on healing and moving forward. Be honest but gentle.',
    aiFirstLine: TEMPLATE_FIRST_LINES.closure,
    typography: {
      headingFont: 'font-serif',
      bodyFont: 'font-serif',
      headingSize: 'text-2xl italic',
      bodySize: 'text-lg leading-loose italic',
    },
    colors: {
      background: 'bg-slate-50 dark:bg-slate-900',
      text: 'text-slate-700 dark:text-slate-300',
      accent: 'text-slate-500 dark:text-slate-400',
    },
    animation: 'typewriter',
  },
  {
    value: 'custom',
    label: 'Custom',
    description: 'Say anything that matters to you.',
    aiPrompt: 'Write a personal, heartfelt message based on the user\'s input. Adapt to their tone and intention. Focus on genuine expression and meaningful connection.',
    aiFirstLine: TEMPLATE_FIRST_LINES.custom,
    typography: {
      headingFont: 'font-heading',
      bodyFont: 'font-sans',
      headingSize: 'text-3xl',
      bodySize: 'text-lg',
    },
    colors: {
      background: 'bg-gradient-to-b from-primary/5 to-primary/10 dark:from-primary/10 dark:to-primary/20',
      text: 'text-foreground',
      accent: 'text-primary',
    },
    animation: 'reveal',
  },
  {
    value: 'birthday',
    label: 'Birthday',
    description: 'Celebrate their special day with heartfelt words.',
    aiPrompt: 'Write a warm birthday message that celebrates who they are. Be specific about what makes them special and express hope for their year ahead.',
    aiFirstLine: TEMPLATE_FIRST_LINES.birthday,
    typography: { headingFont: 'font-heading', bodyFont: 'font-sans', headingSize: 'text-3xl', bodySize: 'text-lg' },
    colors: { background: 'bg-gradient-to-br from-amber-50 to-rose-50 dark:from-amber-950/20 dark:to-rose-950/20', text: 'text-amber-900 dark:text-amber-100', accent: 'text-amber-700 dark:text-amber-400' },
    animation: 'reveal',
  },
  {
    value: 'anniversary',
    label: 'Anniversary',
    description: 'Honor your journey together with elegant words.',
    aiPrompt: 'Write an elegant anniversary message that reflects on the journey together. Be romantic and specific about shared moments. Use warm, timeless language.',
    aiFirstLine: TEMPLATE_FIRST_LINES.anniversary,
    typography: { headingFont: 'font-serif', bodyFont: 'font-serif', headingSize: 'text-3xl', bodySize: 'text-lg' },
    colors: { background: 'bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-950/20 dark:to-yellow-950/20', text: 'text-amber-900 dark:text-amber-100', accent: 'text-amber-700 dark:text-amber-400' },
    animation: 'reveal',
  },
  {
    value: 'celebration',
    label: 'Celebration',
    description: 'Celebrate an achievement or special moment.',
    aiPrompt: 'Write a celebratory message that honors an achievement or milestone. Be energetic and specific about what makes this moment worth celebrating.',
    aiFirstLine: TEMPLATE_FIRST_LINES.celebration,
    typography: { headingFont: 'font-heading', bodyFont: 'font-sans', headingSize: 'text-3xl', bodySize: 'text-lg' },
    colors: { background: 'bg-gradient-to-br from-violet-50 to-sky-50 dark:from-violet-950/20 dark:to-sky-950/20', text: 'text-slate-800 dark:text-slate-100', accent: 'text-primary' },
    animation: 'reveal',
  },
  {
    value: 'encouragement',
    label: 'Encouragement',
    description: 'Uplift someone with positive energy and "you got this" vibes.',
    aiPrompt: 'Write an uplifting, energizing encouragement message that makes the reader feel seen and empowered. Use vivid, positive language. Focus on their strength, resilience, and potential. Make it feel like a warm hug and a rallying cry combined.',
    aiFirstLine: TEMPLATE_FIRST_LINES.encouragement,
    typography: { headingFont: 'font-heading', bodyFont: 'font-sans', headingSize: 'text-3xl', bodySize: 'text-lg' },
    colors: { background: 'bg-gradient-to-br from-emerald-50 via-cyan-50 to-sky-100 dark:from-emerald-950/20 dark:via-cyan-950/20 dark:to-sky-950/20', text: 'text-emerald-900 dark:text-emerald-100', accent: 'text-cyan-600 dark:text-cyan-400' },
    animation: 'reveal',
  },
];

// Helper to get template config by type (handles legacy templates too)
export function getTemplateConfig(type: TemplateType | string): TemplateConfig {
  // Map legacy templates to new emotional intent categories
  const legacyMap: Record<string, TemplateType> = {
    // Old legacy
    classic: 'gratitude',
    modern: 'love',
    bold: 'hard_conversation',
    journal: 'closure',
    // Previous premium templates
    letter: 'gratitude',
    headline: 'love',
    moment: 'love',
    reflection: 'closure',
    keepsake: 'gratitude',
  };
  
  const mappedType = legacyMap[type] || type;
  return TEMPLATE_OPTIONS.find(t => t.value === mappedType) || TEMPLATE_OPTIONS[0];
}
